import React, { useState, createContext, useCallback } from 'react'

export const ToastsContext = createContext(null)

const TOAST_DURATIONS = {
  info: 3000,
  success: 3000,
  completion: 3000,
  warning: 3000,
  error: null,
}

/**
 * Generate unique toast ID
 * @returns {string} Unique ID combining timestamp and random number
 */
const generateToastId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

const ToastsContextProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])

  /**
   * Remove a specific toast by ID
   * @param {string} toastId - Unique toast identifier
   */
  const removeToast = useCallback((toastId) => {
    setToasts((prevToasts) =>
      prevToasts.filter((toast) => toast.id !== toastId)
    )
  }, [])

  /**
   * Add a toast with a specific type
   * @param {string} message - Toast message
   * @param {string} type - Toast type ('success', 'info', 'warning', 'error', 'completion')
   * @param {object} options - Additional options (i18nParams for future i18n support)
   */
  const addToast = useCallback((message, type = 'info', options = {}) => {
    // Difesa: un oggetto (es. risposta API {error, details}) renderizzato
    // come child fa crashare React. Coercizione a stringa leggibile.
    if (
      message !== null &&
      typeof message === 'object' &&
      !React.isValidElement(message)
    ) {
      message =
        message.message ||
        message.error ||
        message.detail ||
        JSON.stringify(message)
    }

    const id = generateToastId()
    const duration = TOAST_DURATIONS[type] ?? null

    const toast = {
      id,
      message,
      type,
      duration,
      i18nParams: options.i18nParams || {},
    }

    setToasts((prevToasts) => [...prevToasts, toast])
  }, [])

  /**
   * Add a success toast
   * @param {string} message - Toast message
   * @param {object} options - Additional options
   */
  const addSuccessToast = useCallback(
    (message, options) => {
      addToast(message, 'success', options)
    },
    [addToast]
  )

  /**
   * Add an info toast
   * @param {string} message - Toast message
   * @param {object} options - Additional options
   */
  const addInfoToast = useCallback(
    (message, options) => {
      addToast(message, 'info', options)
    },
    [addToast]
  )

  /**
   * Add a warning toast
   * @param {string} message - Toast message
   * @param {object} options - Additional options
   */
  const addWarningToast = useCallback(
    (message, options) => {
      addToast(message, 'warning', options)
    },
    [addToast]
  )

  /**
   * Add an error toast
   * @param {string} message - Toast message
   * @param {object} options - Additional options
   */
  const addErrorToast = useCallback(
    (message, options) => {
      addToast(message, 'error', options)
    },
    [addToast]
  )

  /**
   * Add a completion toast
   * @param {string} message - Toast message
   * @param {object} options - Additional options
   */
  const addCompletionToast = useCallback(
    (message, options) => {
      addToast(message, 'completion', options)
    },
    [addToast]
  )

  return (
    <ToastsContext.Provider
      value={{
        toasts,
        setToasts,
        addToast,
        removeToast,
        addSuccessToast,
        addInfoToast,
        addWarningToast,
        addErrorToast,
        addCompletionToast,
      }}
    >
      {children}
    </ToastsContext.Provider>
  )
}

export default ToastsContextProvider
